import {getCart} from '@/cart/cart-store';
import PackOptionButton from '@/components/buy-stack/PackOptionButton';
import DeliverySelectorLabel from '@/experiments/product-select-v3/DeliverySelectorLabel';
import SelectorItemBox from '@/experiments/product-select-v3/SelectorItemBox';
import {getLocalizedStrings} from '@/localization/localization-store';
import {useLocale} from '@/localization/use-locale';
import {
  getPackVariant,
  getProductSelection,
  selectOption,
  selectPlan,
} from '@/product/product-selection';
import {
  getPackCompareAtPrice,
  getPackSavingsPercent,
} from '@/product/product-selection/get-pack-savings-percent';
import {
  getPackOptionName,
  isSizeOption,
  parsePackSize,
  SizeOptionName,
} from '@/product/product-selection/pack-size';
import {batch} from '@preact/signals';
import {useState} from 'preact/hooks';

export default PackOptionSelectorBox;

function PackOptionSelectorBox({
  formatMoneyWithDecimals = false,
}: PackOptionSelectorBoxProps) {
  const {onetimePlan, packOptions, product, selectedPlan, selectedVariant} =
    getProductSelection();

  const strings = getLocalizedStrings();
  const onetimePurchaseLabel =
    strings.buyStackOnetimePurchaseLabel ?? 'One-Time Purchase';
  const saveLabel = strings.buyStackSaveLabel ?? 'Save';

  const badges = [
    {
      packSize: 3,
      badge: strings.buyStackMostPopular ?? 'Most Popular',
      badgeColor: 'accent-red',
    },
    {
      packSize: 12,
      badge: strings.buyStackBestValue ?? 'Best Value',
      badgeColor: 'dark-ocean',
    },
  ];

  const [selectedPackSize, setSelectedPackSize] = useState<string | null>(
    packOptions[0] ?? null
  );

  const selectedColor = selectedVariant?.accentColor ?? 'primary-blue';

  const cart = getCart();
  const currency = cart?.currency ?? 'USD';
  const locale = useLocale();
  const packOptionName = product
    ? getPackOptionName(product)
    : SizeOptionName.PackSize;

  const handleClickOneTime = () => {
    batch(() => {
      selectPlan(onetimePlan);
      if (selectedPackSize) {
        selectOption({
          name: packOptionName,
          value: selectedPackSize,
        });
      }
    });
  };

  const handleClickPackSize = (value: string) => {
    setSelectedPackSize(value);
    selectOption({name: packOptionName, value});
  };

  return product && selectedVariant ? (
    <SelectorItemBox
      dataTestid="one-time-purchase"
      color={selectedColor}
      showBackgroundColor={true}
      label={
        <DeliverySelectorLabel
          compareAtPrice={getPackCompareAtPrice(
            selectedPackSize ?? '1',
            product
          )}
          currency={currency}
          locale={locale}
          formatMoneyWithDecimals={formatMoneyWithDecimals}
          price={getPackVariant(selectedPackSize ?? '1 Pack')?.price ?? 0}
        >
          {onetimePurchaseLabel}
        </DeliverySelectorLabel>
      }
      isSelected={selectedPlan === onetimePlan}
      onClick={handleClickOneTime}
    >
      {packOptions.length > 1 && (
        <div className="flex gap-x-2 pt-2">
          {packOptions.map((option) => {
            const selected =
              selectedVariant.selectedOptions.find((option) =>
                isSizeOption(option, product.sizeOptionNames)
              )?.value === option;

            const packSize = parsePackSize(option);

            const savings = Math.round(
              getPackSavingsPercent(option, product) * 100
            );

            const badge = badges.find((badge) => badge.packSize === packSize);

            // Format the rest of the option label
            // for display below the pack size
            const bottom = option
              .toUpperCase()
              .replace(packSize.toString(), '')
              .trim()
              .split(' ')[0];

            const soldOut = !getPackVariant(option)?.available;
            const className = soldOut ? 'line-through' : '';

            return (
              <PackOptionButton
                badge={badge?.badge}
                badgeColor={badge?.badgeColor}
                bottom={<span className={className}>{bottom}</span>}
                color={selectedColor}
                dataTestId={`pack-size-${packSize}`}
                isSelected={selected}
                key={option}
                middle={<span className={className}>{packSize}</span>}
                onClick={() => handleClickPackSize(option)}
                top={
                  <span className={className}>
                    {saveLabel} {savings}%
                  </span>
                }
              />
            );
          })}
        </div>
      )}
    </SelectorItemBox>
  ) : (
    <></>
  );
}

export type PackOptionSelectorBoxProps = {
  formatMoneyWithDecimals?: boolean;
};
